import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { CheckCircle, Sparkle, Sun, MoonStars, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Super-admin page for flipping the live US-audience theme.
 * Indian visitors are never affected — see backend themes.py / ThemeContext.
 */

const THEMES = [
  {
    id: "royal_amber",
    name: "Royal Amber",
    tagline: "The classic warm experience — gold, ivory, ember.",
    description: "Warm amber & ivory palette with serif display headings. The current default — every visitor sees this unless a different theme is flipped on below.",
    icon: Sun,
    swatch: ["#fbbf24", "#f97316", "#e11d48", "#FDF7EA"],
    previewClass: "bg-gradient-to-br from-amber-100 via-orange-100 to-rose-100 text-[#3F2D1B]",
  },
  {
    id: "cosmic_night",
    name: "Cosmic Night Sky",
    tagline: "Deep indigo, twinkling stars, drifting planets.",
    description: "Vibrant blue night-sky theme with an animated star field, floating planets, glassy translucent cards, and cyan→violet gradient buttons. Designed to feel premium and mystical for the US audience.",
    icon: MoonStars,
    swatch: ["#060a23", "#4f8cff", "#a78bfa", "#fbbf24"],
    previewClass: "bg-gradient-to-br from-[#060a23] via-[#0a1138] to-[#1e1b4b] text-[#e8eaff]",
  },
];

export default function AdminThemesPage() {
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(null);   // theme id being flipped
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const load = () => {
    setLoading(true);
    api.get("/super-admin/themes/active-us")
      .then(({ data }) => {
        setActive(data?.theme || "royal_amber");
        setMeta({ updated_at: data?.updated_at, updated_by: data?.updated_by });
      })
      .catch(() => toast.error("Couldn't load theme settings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const activate = async (id) => {
    if (id === active) return;
    setSaving(id);
    try {
      const { data } = await api.post("/super-admin/themes/active-us", { theme: id });
      setActive(data?.theme || id);
      setMeta({ updated_at: data?.updated_at, updated_by: data?.updated_by });
      const friendly = THEMES.find((t) => t.id === id)?.name || id;
      toast.success(`Activated "${friendly}" for US visitors. Live within 60s.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update theme");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF5ED] py-8 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h1 className="font-serif-display text-3xl text-[#3F2D1B]" data-testid="admin-themes-title">
              Site theme — US audience only
            </h1>
            <p className="text-sm text-stone-700 mt-1">
              Flip the live theme served to visitors whose IP resolves to the US. Indian
              visitors and visitors from every other country always see Royal Amber — they
              never experience these changes.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 py-2 text-xs font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
            data-testid="admin-themes-refresh"
          >
            <ArrowsClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>

        {/* Current state banner */}
        <div className="mt-4 rounded-2xl border border-amber-200 bg-white px-5 py-4 flex items-center gap-3 flex-wrap" data-testid="admin-themes-current">
          <CheckCircle size={20} weight="fill" className="text-emerald-600" />
          <div className="text-sm text-stone-800">
            <span className="font-semibold">Currently active for US visitors: </span>
            <span className="text-amber-700 font-bold">{THEMES.find((t) => t.id === active)?.name || "—"}</span>
          </div>
          {meta.updated_at && (
            <div className="text-[11px] text-stone-500 ml-auto">
              Last changed {new Date(meta.updated_at).toLocaleString()} {meta.updated_by ? `by ${meta.updated_by}` : ""}
            </div>
          )}
        </div>

        {/* Theme grid */}
        <div className="mt-6 grid sm:grid-cols-2 gap-5">
          {THEMES.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.id;
            const isSaving = saving === t.id;
            return (
              <div
                key={t.id}
                className={`relative overflow-hidden rounded-3xl border shadow-sm transition-all ${
                  isActive ? "border-amber-400 ring-2 ring-amber-300/60 shadow-lg" : "border-stone-200 hover:shadow-md"
                }`}
                data-testid={`admin-theme-card-${t.id}`}
              >
                {/* Preview tile */}
                <div className={`relative h-44 ${t.previewClass} flex items-center justify-center overflow-hidden`}>
                  {t.id === "cosmic_night" && (
                    // Tiny star sparkles + planet for the preview tile only
                    <>
                      <div className="absolute inset-0 opacity-90"
                        style={{
                          backgroundImage:
                            "radial-gradient(1.5px 1.5px at 20% 30%, #ffffff 50%, transparent 60%)," +
                            "radial-gradient(2px 2px at 75% 20%, #fbbf24 50%, transparent 60%)," +
                            "radial-gradient(1.5px 1.5px at 30% 70%, #c4b5fd 50%, transparent 60%)," +
                            "radial-gradient(1.5px 1.5px at 80% 80%, #ffffff 50%, transparent 60%)," +
                            "radial-gradient(2px 2px at 50% 50%, #93c5fd 50%, transparent 60%)",
                        }}
                      />
                      <div className="absolute top-6 right-8 w-12 h-12 rounded-full"
                        style={{
                          background: "radial-gradient(circle at 30% 25%, #c7d2fe 0%, #6366f1 40%, #1e1b4b 80%)",
                          boxShadow: "0 0 24px rgba(167,139,250,0.45), inset -6px -8px 18px rgba(0,0,0,0.5)",
                        }}
                      />
                    </>
                  )}
                  <Icon size={56} weight="duotone" className="relative z-10 drop-shadow" />
                </div>

                {/* Body */}
                <div className="p-5 bg-white">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-serif-display text-xl text-[#3F2D1B]">{t.name}</h2>
                    {isActive && (
                      <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                        Live
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-stone-700 font-medium italic">{t.tagline}</p>
                  <p className="text-sm text-stone-600 mt-3 leading-relaxed">{t.description}</p>

                  {/* Colour swatch */}
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-[10px] uppercase tracking-widest text-stone-400">Palette</span>
                    {t.swatch.map((c) => (
                      <span
                        key={c}
                        className="inline-block h-5 w-5 rounded-full border border-stone-300"
                        style={{ backgroundColor: c }}
                        title={c}
                      />
                    ))}
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => activate(t.id)}
                    disabled={isActive || isSaving}
                    className={`mt-5 w-full rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                      isActive
                        ? "bg-emerald-100 text-emerald-700 cursor-default"
                        : "bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white hover:opacity-95 shadow-md"
                    } disabled:opacity-60`}
                    data-testid={`admin-theme-activate-${t.id}`}
                  >
                    {isActive ? "Currently active" : isSaving ? "Activating…" : `Activate "${t.name}"`}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-xs text-stone-500 flex items-start gap-2">
          <Sparkle size={14} weight="duotone" className="text-amber-600 mt-0.5 flex-none" />
          <span>
            Switching themes is reversible at any time — just tap <span className="font-medium">"Activate Royal Amber"</span> to revert.
            All US visitors will see the change within 60 seconds of the next page load (no reload needed).
          </span>
        </p>
      </div>
    </div>
  );
}
